<div class="container-fluid complyon-page itsec-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell itsec-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title itsec-title mb-2 text-l">IT security</h1>
      <p class="complyon-subtitle itsec-subtitle mb-0 text-s">Access reviews, MFA, privileged accounts, backups, vulnerabilities and change controls.</p>
    </section>

    <section class="d-flex align-items-start justify-content-between gap-3 mb-3">
      <div class="d-inline-flex flex-wrap align-items-center gap-1 rounded-4 p-1 border border-light-subtle bg-white" role="tablist" aria-label="IT security tabs">
        <button
          v-for="tab in it_tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <button type="button" class="btn config-navbar-secondary-action d-inline-flex align-items-center gap-2">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'overview'">
      <section class="complyon-banner rounded-4 px-3 py-3 mb-3">
        <div class="d-flex align-items-start gap-2">
          <i class="bi bi-shield-check text-success mt-1"></i>
          <p class="mb-0 text-s"><strong>IT governance, not tooling.</strong> Complyon doesn't replace your security stack - it proves the governance controls over it are operating, with evidence auditors can rely on.</p>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div v-for="card in overview_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-success'">{{ card.value }}</div>
            <div class="complyon-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div class="col-12 col-xl-7">
          <article class="complyon-panel rounded-4 overflow-hidden h-100">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
              <div>
                <div class="complyon-section-title text-s">IT control register</div>
                <div class="complyon-section-subtitle text-xs">Governance controls and whether each is operating with current evidence.</div>
              </div>
              <button type="button" class="btn btn-sm config-navbar-secondary-action">View all</button>
            </div>
            <div class="table-responsive">
              <table class="table complyon-table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3 text-xs">Control</th>
                    <th class="text-xs">Framework</th>
                    <th class="text-xs">Last test</th>
                    <th class="text-xs">Status</th>
                    <th class="pe-3"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in overview_controls" :key="row.control">
                    <td class="ps-3"><div class="fw-semibold">{{ row.control }}</div></td>
                    <td>{{ row.framework }}</td>
                    <td>{{ row.last_test }}</td>
                    <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                    <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>

        <div class="col-12 col-xl-5">
          <article class="complyon-panel rounded-4 overflow-hidden h-100">
            <div class="px-3 pt-3 pb-2">
              <div class="complyon-section-title text-s">MFA compliance</div>
              <div class="complyon-section-subtitle text-xs">Multi-factor coverage</div>
            </div>
            <div class="px-3 pb-3">
              <div class="text-center mb-2">
                <div class="display-6 fw-bold text-success mb-0">94%</div>
                <div class="text-xs text-secondary">workforce covered</div>
              </div>
              <div class="d-flex align-items-center gap-3 mb-2 text-xs">
                <div class="flex-shrink-0" style="width: 56px;">All staff</div>
                <div class="flex-grow-1">
                  <div class="progress">
                    <div class="progress-bar bg-success" style="width: 94%;"></div>
                  </div>
                </div>
                <div class="text-end" style="width: 76px;">94% <span class="text-secondary">198/210</span></div>
              </div>
              <div class="d-flex align-items-center gap-3 mb-3 text-xs">
                <div class="flex-shrink-0" style="width: 56px;">Privileged</div>
                <div class="flex-grow-1">
                  <div class="progress">
                    <div class="progress-bar bg-success" style="width: 100%;"></div>
                  </div>
                </div>
                <div class="text-end" style="width: 76px;">100% <span class="text-secondary">16/16</span></div>
              </div>
              <div class="border-top border-light-subtle pt-2">
                <div class="d-flex align-items-center gap-2 text-xs mb-2"><i class="bi bi-info-circle text-secondary"></i><span>12 active exceptions (service accounts & legacy) - tracked & time-bound.</span></div>
                <div class="d-flex align-items-center gap-2 text-xs"><i class="bi bi-exclamation-triangle text-secondary"></i><span>1 vendor review overdue</span></div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="px-3 pt-3 pb-2">
          <div class="complyon-section-title text-s">Connected modules</div>
          <div class="complyon-section-subtitle text-xs">IT governance is not a silo - every control, review and piece of evidence links across the platform. Open any connected register.</div>
        </div>
        <div class="px-3 pb-3">
          <div class="row g-2">
            <div v-for="module in connected_modules" :key="module.title" class="col-12 col-md-6">
              <div class="d-flex align-items-start justify-content-between gap-2 rounded-3 border border-light-subtle px-3 py-2">
                <div>
                  <div class="text-xs text-uppercase text-secondary">{{ module.title }}</div>
                  <div class="text-xs fw-semibold">{{ module.copy }}</div>
                </div>
                <i class="bi bi-box-arrow-up-right text-secondary flex-shrink-0"></i>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'control-register'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">IT control register</div>
            <div class="complyon-section-subtitle text-xs">Governance controls mapped to a framework, with the evidence that proves they operate.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New control</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Control</th>
                <th class="text-xs">Domain</th>
                <th class="text-xs">Framework</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Last test</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in control_rows" :key="row.ref">
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold">{{ row.control }}</td>
                <td>{{ row.domain }}</td>
                <td>{{ row.framework }}</td>
                <td>{{ row.owner }}</td>
                <td>{{ row.last_test }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'access-reviews'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">System & privileged access reviews</div>
            <div class="complyon-section-subtitle text-xs">Periodic certification that the right people have the right access - privileged access flagged.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New review</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Scope</th>
                <th class="text-xs">Type</th>
                <th class="text-xs">Reviewer</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">Findings</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in access_rows" :key="row.scope">
                <td class="ps-3 fw-semibold">{{ row.scope }}</td>
                <td>{{ row.type }}</td>
                <td>{{ row.reviewer }}</td>
                <td>{{ row.due }}</td>
                <td>{{ row.findings }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'asset-register'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">Asset register</div>
            <div class="complyon-section-subtitle text-xs">IT assets, their classification and the governance controls applied to each.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New asset</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Asset</th>
                <th class="text-xs">Class</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Location</th>
                <th class="text-xs">Controls</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in asset_rows" :key="row.asset">
                <td class="ps-3 fw-semibold">{{ row.asset }}</td>
                <td><span class="badge complyon-badge bg-light text-secondary">{{ row.class }}</span></td>
                <td>{{ row.owner }}</td>
                <td>{{ row.location }}</td>
                <td>{{ row.controls }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'backup-dr'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">Backup, patch & DR evidence</div>
            <div class="complyon-section-subtitle text-xs">Proof that resilience and vulnerability controls operated - the gaps auditors flag first.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New evidence</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Type</th>
                <th class="text-xs">Item</th>
                <th class="text-xs">Period</th>
                <th class="text-xs">Provided</th>
                <th class="text-xs">Verified by</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in backup_rows" :key="row.type + row.item">
                <td class="ps-3"><span class="badge complyon-badge bg-light text-secondary">{{ row.type }}</span></td>
                <td class="fw-semibold">{{ row.item }}</td>
                <td>{{ row.period }}</td>
                <td>{{ row.provided }}</td>
                <td>{{ row.verified_by }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'vendor-reviews'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">Vendor security reviews</div>
            <div class="complyon-section-subtitle text-xs">Security assurance over IT vendors - assurance type, last review and residual risk.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>New vendor review</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Vendor</th>
                <th class="text-xs">Service</th>
                <th class="text-xs">Assurance</th>
                <th class="text-xs">Reviewed</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in vendor_rows" :key="row.vendor">
                <td class="ps-3 fw-semibold">{{ row.vendor }}</td>
                <td>{{ row.service }}</td>
                <td>{{ row.assurance }}</td>
                <td>{{ row.reviewed }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.risk.class">{{ row.risk.label }}</span></td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'incidents'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">Security incidents</div>
            <div class="complyon-section-subtitle text-xs">Security incidents escalate to the case register.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn">
            <i class="bi bi-plus-lg"></i>
            <span>Log incident</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Incident</th>
                <th class="text-xs">Severity</th>
                <th class="text-xs">Reported</th>
                <th class="text-xs">Stage</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in incident_rows" :key="row.ref">
                <td class="ps-3 fw-semibold text-success">{{ row.ref }}</td>
                <td class="fw-semibold">{{ row.incident }}</td>
                <td>{{ row.severity }}</td>
                <td>{{ row.reported }}</td>
                <td>{{ row.stage }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'policy-acks'">
      <section class="complyon-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3 pb-2">
          <div>
            <div class="complyon-section-title text-s">Policy acknowledgements</div>
            <div class="complyon-section-subtitle text-xs">Cyber-awareness training & policy acknowledgements.</div>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Policy</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Next review</th>
                <th class="text-xs">Acknowledged</th>
                <th class="text-xs">Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in policy_rows" :key="row.policy">
                <td class="ps-3 fw-semibold">{{ row.policy }}</td>
                <td>{{ row.owner }}</td>
                <td>{{ row.next_review }}</td>
                <td>{{ row.acknowledgement }}</td>
                <td><span class="badge complyon-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </div>
</div>
